import { Box, SimpleGrid, Flex, HStack, useBreakpointValue, Button } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BsArrowUpRightSquareFill } from 'react-icons/bs';
import { NewsCard } from '../molecules/NewsCard';
import { CustomText } from '../atoms/CustomText';
import { appInsights } from '../../appInsights';
import { generateSlug } from '../../utils';

export interface NewsArticle {
  imageUrl?: string;
  title: string;
  createdDate: string;
  updatedDate: string;
  publishedDate: string;
  id: string;
  content?: string;
  enable?: string;
  writtenBy?: string;
  pin?: string;
}

export interface NewsSectionProps {
  newsArticles: NewsArticle[];
  newsArticleTextColor?: string;
  newsSectionHeadingTextColor?: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  gradientAccentColor?: string;
  newsHeadlineTextColor?: string;
  newsArticlesTranslations: NewsArticle[];
  newsSectionHeadingText: string;
  newsSectionHelperTextView: string;
  newsSectionHelperTextReadMore: string;
}

export const NewsSection: React.FC<NewsSectionProps> = ({
  newsArticles,
  newsArticleTextColor,
  newsSectionHeadingTextColor,
  primaryAccentColor,
  secondaryAccentColor,
  gradientAccentColor,
  newsHeadlineTextColor,
  newsArticlesTranslations,
  newsSectionHeadingText,
  newsSectionHelperTextView,
  newsSectionHelperTextReadMore,
}) => {
  const navigate = useNavigate();
  const translatedNewsArticle = newsArticles.map(item => {
    const itemTranslation = newsArticlesTranslations.find(t => t.id === item.id);
    return {
      ...item, // Keep all properties from `items`
      title: itemTranslation?.title ?? item.title, // Override `title` if translation exists
    };
  });

  const currentArticles = translatedNewsArticle;

  const handleShowAll = () => {
    if (newsArticles.length === 0) return; // safety check
    const firstArticle = newsArticles[0];
    const slug = generateSlug(firstArticle.title);
    navigate(`/news/${firstArticle.id}-${slug}`);
  };

  const showAllHref = () => {
    if (newsArticles.length === 0) return ''; // safety check
    const firstArticle = newsArticles[0];
    const slug = generateSlug(firstArticle.title);
    return `/news/${firstArticle.id}-${slug}`;
  };

  const onClickNewsItem = (id: string, title: string, date: string) => {
    const slug = generateSlug(title);
    appInsights.trackEvent({
      name: 'landingPage',
      properties: {
        buttonName: 'newsArticleTile',
        userName: 'anonymous',
        publishId: id,
        newsArticleTitle: title,
        newsArticleDate: date,
      },
    });
    navigate(`/news/${id}-${slug}`);
  };

  const newsItemHref = (id: string, title: string) => {
    const slug = generateSlug(title);
    return `/news/${id}-${slug}`;
  };

  // to decide how many articles to show
  const articlesToShow = useBreakpointValue({ base: 3, md: 6 });

  return (
    <Box py="10" marginTop="2vh">
      <Flex justifyContent="space-between" alignItems="center" mt="6">
        <CustomText
          variant="h1"
          fontSize={{ base: 'xl', sm: '2xl', md: '3xl' }}
          // fontSize={{
          //   base: 'xl',
          //   sm: '2xl',
          //   md: '3xl',
          //   lg: '3xl',
          //   xl: '3xl',
          //   '2xl': '5xl',
          //   '3xl': '5xl',
          //   '4xl': '6xl',
          // }}
          // fontSize="3xl"
          fontWeight="bold"
          mb="6"
          color={newsSectionHeadingTextColor}
        >
          {newsSectionHeadingText}
        </CustomText>

        <a href={showAllHref()}>
          <Button
            onClick={e => {
              const isLeftClick = e.button === 0;
              const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
              // SPA routing on normal click
              if (isLeftClick && !isModifiedClick) {
                e.preventDefault();
                handleShowAll();
              }
            }}
            variant="link"
            color={newsSectionHeadingTextColor}
          >
            <HStack spacing={1} align="center">
              <Box as="span" fontWeight="600">
                {newsSectionHelperTextView}
              </Box>
              <BsArrowUpRightSquareFill color={secondaryAccentColor} />
            </HStack>
          </Button>
        </a>
      </Flex>

      <Box height="4px" width="60px" background={gradientAccentColor} mb="6" />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="6" mt="4vh">
        {(() => {
          const enabledArticles =
            currentArticles?.filter(article => article.enable === 'true') || [];

          const pinned = enabledArticles
            .filter(article => article.pin === 'true')
            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());

          const others = enabledArticles
            .filter(article => article.pin !== 'true')
            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());

          // Pinned first, then others → only take 6 total
          const displayArticles = [...pinned, ...others].slice(0, articlesToShow);

          return displayArticles.map(article => {
            const formattedDate = new Date(article.createdDate).toLocaleDateString('en-US', {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
            });
            // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3');

            return (
              <NewsCard
                key={article.id}
                imageUrl={article.imageUrl}
                title={article.title}
                date={formattedDate}
                bgColor={primaryAccentColor}
                secondaryAccentColor={secondaryAccentColor}
                textColor={newsArticleTextColor}
                id={article.id}
                onClickNewsItem={() => onClickNewsItem(article.id, article.title, formattedDate)}
                newsHeadlineTextColor={newsHeadlineTextColor}
                newsSectionHelperTextReadMore={newsSectionHelperTextReadMore}
                newsItemHref={newsItemHref(article.id, article.title)}
              />
            );
          });
        })()}
      </SimpleGrid>

      {/* <SimpleGrid columns={{ base: 1, md: 3 }} spacing="6" mt="4vh">
        {(() => {
          const enabledArticles =
            currentArticles?.filter(article => article.enable === 'true') || [];

          const articles = enabledArticles.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );

          // Pinned first, then others → only take 6 total
          const displayArticles = articles.slice(0, articlesToShow);

          return displayArticles.map(article => {
            const formattedDate = new Date(article.date)
              .toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              })
              .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3');

            return (
              <NewsCard
                key={article.id}
                imageUrl={article.imageUrl}
                title={article.title}
                date={formattedDate}
                bgColor={primaryAccentColor}
                secondaryAccentColor={secondaryAccentColor}
                textColor={newsArticleTextColor}
                id={article.id}
                onClickNewsItem={() => onClickNewsItem(article.id, article.title, formattedDate)}
                newsHeadlineTextColor={newsHeadlineTextColor}
                newsSectionHelperTextReadMore={newsSectionHelperTextReadMore}
              />
            );
          });
        })()}
      </SimpleGrid> */}
    </Box>
  );
};
